import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Pencil, Check, Loader2, ImageIcon, AlertTriangle } from "lucide-react";
import { useVanDesign } from "./VanDesignContext";
import { vanDesignApi, streamVanAsset } from "@/api/vanDesign";
import { VanHeader, NCard, NBtn, MonoBadge } from "./vanUI";
import { catalogOf } from "./vanData";
import VanSchematic from "./VanSchematic";

export default function VanDesignAIDraft({ onBack }) {
  const { project, setVan, setModules, setAI, setScreen, photos } = useVanDesign();
  const [busy, setBusy] = useState(false);
  const [hero, setHero] = useState(null);
  const [heroBusy, setHeroBusy] = useState(false);
  const ran = useRef(false);

  const run = async () => {
    setBusy(true);
    try {
      const d = await vanDesignApi.draft({
        template_id: project.van?.templateId || null,
        interior: project.van?.interior || null,
        goals: project.goals,
        photos: photos || [],
      });
      // keep interior authoritative from backend clamp
      if (d.interior) setVan({ ...project.van, interior: d.interior });
      setModules(d.modules || []);
      setAI({
        reasoning: d.reasoning, assumptions: d.assumptions, tradeoffs: d.tradeoffs,
        clearances: d.clearances, build_sequence: d.build_sequence, fallback: d.ai_fallback,
      });
      if (d.ai_fallback) toast("AI offline — showing a built-in draft you can edit");
    } catch (e) {
      toast.error("Draft failed — try again");
    } finally { setBusy(false); }
  };

  const genHero = async () => {
    setHeroBusy(true);
    try {
      const r = await vanDesignApi.hero({ template_id: project.van?.templateId || null, goals: project.goals });
      if (r.ok && r.asset_id) {
        const url = await streamVanAsset(r.asset_id);
        setHero(url); setAI({ hero_asset_id: r.asset_id });
      } else { toast("Concept image unavailable — schematic is the plan of record"); }
    } catch { toast("Concept image unavailable"); }
    finally { setHeroBusy(false); }
  };

  useEffect(() => { if (!ran.current) { ran.current = true; run(); } }, []);

  const ai = project.ai;
  const seq = ai.build_sequence || [];

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-[#FDFBF7] max-w-[412px] mx-auto" data-testid="van-draft">
      <VanHeader title="AI Draft Layout" sub={ai.fallback ? "Built-in planner" : "OpenAI GPT-5 vision"} onBack={onBack} step="AI" testid="van-draft-header" />
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 space-y-4">
        {busy ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 size={26} className="animate-spin text-black" />
            <p className="font-mono text-[11px] uppercase tracking-widest text-black/60">Designing your layout…</p>
          </div>
        ) : (
          <>
            {ai.fallback && (
              <NCard className="p-2.5 flex items-center gap-2 bg-[#FFF4D6]">
                <AlertTriangle size={14} /><p className="font-mono text-[9px] text-black/70">AI service offline — deterministic draft shown. Fully editable.</p>
              </NCard>
            )}

            {/* Top-down schematic (source of truth) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/60">Top-down plan (editable source)</p>
                <MonoBadge tone="gold">{project.modules?.length || 0} modules</MonoBadge>
              </div>
              <VanSchematic van={project.van} modules={project.modules} editable={false} maxW={372} maxH={420} onSelect={() => {}} />
            </div>

            {/* AI hero concept */}
            <NCard className="p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="font-mono text-[10px] uppercase tracking-widest text-black/60">Concept preview</p>
                <MonoBadge>visual only</MonoBadge>
              </div>
              {hero ? (
                <img src={hero} alt="concept" className="w-full rounded-lg border-2 border-black" data-testid="van-hero-img" />
              ) : (
                <NBtn variant="ghost" onClick={genHero} disabled={heroBusy} testid="van-hero-gen">
                  {heroBusy ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />} Generate concept image
                </NBtn>
              )}
            </NCard>

            {/* Reasoning */}
            <NCard className="p-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-black/60 mb-1.5">Why this layout</p>
              <p className="font-mono text-[11px] text-black/80 leading-relaxed" data-testid="van-reasoning">{ai.reasoning}</p>
            </NCard>

            <div className="grid grid-cols-1 gap-3">
              {[["Assumptions", ai.assumptions], ["Trade-offs", ai.tradeoffs], ["Clearances", ai.clearances]].map(([t, arr]) => (
                (arr && arr.length > 0) && (
                  <NCard key={t} className="p-3">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-black/60 mb-1.5">{t}</p>
                    <ul className="space-y-1">
                      {arr.map((x, i) => <li key={i} className="font-mono text-[10px] text-black/75 flex gap-1.5"><span className="text-[#D4AF37]">▪</span>{x}</li>)}
                    </ul>
                  </NCard>
                )
              ))}
            </div>

            {/* Build sequence */}
            {seq.length > 0 && (
              <NCard className="p-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-black/60 mb-2">Recommended build order</p>
                <div className="flex flex-wrap gap-1.5">
                  {seq.map((t, i) => (
                    <span key={i} className="font-mono text-[9px] font-black uppercase px-2 py-1 rounded border-2 border-black bg-white">
                      {i + 1}. {catalogOf(t)?.label || t}
                    </span>
                  ))}
                </div>
              </NCard>
            )}
          </>
        )}
      </div>
      {!busy && (
        <div className="shrink-0 border-t-2 border-black p-3 bg-[#FDFBF7] grid grid-cols-3 gap-2">
          <NBtn variant="ghost" onClick={run} testid="van-draft-regen"><RefreshCw size={13} /> Regen</NBtn>
          <NBtn variant="ghost" onClick={() => setScreen("editor")} testid="van-draft-edit"><Pencil size={13} /> Edit</NBtn>
          <NBtn variant="gold" onClick={() => { toast.success("Draft accepted"); setScreen("editor"); }} testid="van-draft-accept"><Check size={13} /> Accept</NBtn>
        </div>
      )}
    </div>
  );
}
